import React, { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { money, fmtDate, STATUS_LABELS } from "@/lib/format";
import { FileText, AlertTriangle } from "lucide-react";
import { scopesFromDoc } from "@/lib/disciplines";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import CommentThread from "@/components/CommentThread";
import { RevisionCompareDialog } from "@/components/RevisionCompareDialog";
import { ScopeEditor } from "@/components/ScopeEditor";
import ScopeCompare from "@/components/ScopeCompare";
import { PriceTrajectoryCard } from "@/components/PriceTrajectoryCard";

const STATUSES = ["lead", "qualified", "estimating", "proposal", "won", "lost", "declined"];
const LOSS_REASONS = ["Price", "Schedule", "Relationship", "Scope mismatch", "No-bid decision"];

export default function DetailsTab({ bid, updateBid, perms, reload }) {
  const [revisions, setRevisions] = useState([]);
  const [activities, setActivities] = useState([]);
  const [sealed, setSealed] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [local, setLocal] = useState({
    capacity_mw: bid.capacity_mw, state: bid.state, location: bid.location,
    due_date: bid.due_date, mobilization_date: bid.mobilization_date,
  });

  useEffect(() => {
    api.get(`/bids/${bid.bid_id}/revisions`).then((r) => setRevisions(r.data)).catch(() => {});
    api.get(`/bids/${bid.bid_id}/sealed-snapshots`).then((r) => setSealed(r.data)).catch(() => {});
    api.get("/activities", { params: { bid_id: bid.bid_id } }).then((r) => setActivities(r.data)).catch(() => {});
  }, [bid.bid_id, bid.revision, bid.status]);

  const changeStatus = async (status) => {
    const patch = { status };
    if (["lost", "declined"].includes(status)) {
      const reason = window.prompt(`Reason for ${status} (recorded on the bid):`, bid.loss_reason || LOSS_REASONS[0]);
      if (reason === null) return;
      patch.loss_reason = reason;
    }
    try { await updateBid(patch); toast.success(`Moved to ${STATUS_LABELS[status]}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update the bid"); }
  };

  const dateVal = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : "");
  const saveField = (field, value) => updateBid({ [field]: value });
  const [iddNonce, setIddNonce] = useState(0);
  const saveInternal = (v) => updateBid({ internal_due_date: v || null }).catch(() => setIddNonce((n) => n + 1));

  const scopes = scopesFromDoc(bid);
  const gateWarn = scopes.length === 0
    ? "Add at least one scope — this bid can’t be sent until a discipline is set."
    : scopes.some((s) => s.completeness === "partial" && !(s.completeness_note || "").trim())
    ? "Add a short note for each partial scope before sending."
    : null;

  return (
    <div className="p-6 grid lg:grid-cols-3 gap-6 max-w-[1800px] w-full mx-auto">
      <div className="lg:col-span-2 space-y-6">
        <Card className="p-5" data-testid="discipline-card">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-1">Scopes &amp; material</h2>
          <p className="text-xs text-slate-400 mb-4">One line per discipline. Mark the lead scope (★, names the project), how much of each scope is yours, and who furnishes material — this keeps your benchmarks apples-to-apples.</p>
          {gateWarn && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4" data-testid="discipline-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{gateWarn}</span>
            </div>
          )}
          <ScopeEditor value={scopes} onChange={(next) => saveField("scopes", next)} disabled={!perms.canPrice} />
        </Card>

        <Card className="p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-4">Lead & project</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Client">{bid.client_name || "—"}</Field>
            <Field label="Discipline scope"><span className="capitalize">{bid.discipline_scope}</span></Field>
            <div>
              <Label className="text-xs text-slate-500">Capacity (MW)</Label>
              <Input type="number" defaultValue={bid.capacity_mw ?? ""} disabled={!perms.canPrice}
                onBlur={(e) => saveField("capacity_mw", e.target.value ? Number(e.target.value) : null)}
                className="h-9 mt-1 font-mono" data-testid="details-mw" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Location</Label>
              <Input defaultValue={bid.location ?? ""} disabled={!perms.canPrice}
                onBlur={(e) => saveField("location", e.target.value)} className="h-9 mt-1" data-testid="details-location" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">RocketFuel account link</Label>
              <Input defaultValue={bid.rocketfuel_url ?? ""} key={`rf-${bid.rocketfuel_url || ""}`} placeholder="https://… (leave blank to use the client's RocketFuel link)"
                onBlur={(e) => e.target.value.trim() !== (bid.rocketfuel_url || "") && saveField("rocketfuel_url", e.target.value.trim()).catch(() => {})}
                className="h-9 mt-1" data-testid="details-rocketfuel-url" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-slate-500">Project folder name <span className="text-slate-300">(optional)</span></Label>
              <Input defaultValue={bid.project_folder ?? ""} key={`pf-${bid.project_folder || ""}`} disabled={!perms.canPrice} placeholder={`e.g. Brazoria II (blank = "${bid.name || "bid name"}")`}
                onBlur={(e) => e.target.value.trim() !== (bid.project_folder || "") && saveField("project_folder", e.target.value.trim()).then(() => toast.success("Project folder saved. Drive folders are renamed to match.")).catch(() => {})}
                className="h-9 mt-1" data-testid="details-project-folder" />
              <p className="text-[10px] text-slate-400 mt-1">Names this project's Google Drive folder (Year / Client / Project). Changing it renames the existing folders.</p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-4">Key dates</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              ["due_date", "Client due date"], ["mobilization_date", "Mobilization (schedule start)"],
            ].map(([f, label]) => (
              <div key={f}>
                <Label className="text-xs text-slate-500">{label}</Label>
                <Input type="date" defaultValue={dateVal(bid[f])} disabled={!perms.canPrice}
                  onBlur={(e) => saveField(f, e.target.value ? new Date(e.target.value).toISOString() : null)}
                  className="h-9 mt-1" data-testid={`details-${f}`} />
              </div>
            ))}
            <div>
              <Label className="text-xs text-slate-500">
                Internal due date {bid.internal_due_date && bid.internal_due_auto !== false && <span className="text-[10px] uppercase tracking-wide text-slate-400" data-testid="details-internal-auto">· auto</span>}
              </Label>
              <Input type="date" key={`idd-${bid.internal_due_date || ""}-${iddNonce}`} defaultValue={(bid.internal_due_date || "").slice(0, 10)} disabled={!perms.canPrice}
                onBlur={(e) => e.target.value !== (bid.internal_due_date || "") && saveInternal(e.target.value)}
                className="h-9 mt-1" data-testid="details-internal_due_date" />
              <p className="text-[11px] text-slate-400 mt-1">Auto-set to 1 business day before the client date. You can move it earlier, never later than 1 day before.</p>
            </div>
            {perms.canPrice && (
              <label className="flex items-start gap-2 text-xs text-slate-500 cursor-pointer select-none" data-testid="details-test-record">
                <input type="checkbox" className="mt-0.5 accent-[#003E87]" checked={!!bid.test}
                  onChange={(e) => saveField("test", e.target.checked).then(() => toast.success(e.target.checked ? "Marked as a test record — hidden from the pipeline and lists" : "Back in the pipeline"))}
                  data-testid="details-test-record-toggle" />
                <span>Test record<br /><span className="text-[11px] text-slate-400">Kept out of the pipeline, lists and the connector unless asked for.</span></span>
              </label>
            )}
          </div>
          {bid.schedule_project_end && (
            <div className="mt-4 text-xs text-slate-500" data-testid="details-timeline">
              Construction window: <span className="font-medium text-slate-700">{fmtDate(bid.mobilization_date || bid.schedule_project_start)}</span> → <span className="font-medium text-slate-700">{fmtDate(bid.schedule_project_end)}</span>{bid.schedule_work_days ? ` · ${bid.schedule_work_days} work-days` : ""}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-3">Context &amp; notes</h2>
          <textarea defaultValue={bid.context_notes || ""} disabled={!perms.canPrice} rows={3}
            placeholder="Bid context — scope quirks, site access, who owns what, anything the team should know…"
            onBlur={(e) => saveField("context_notes", e.target.value)}
            className="w-full text-sm border border-slate-200 rounded px-3 py-2 resize-none disabled:opacity-60" data-testid="bid-context-notes" />
        </Card>

        <Card className="p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-3">Comments</h2>
          <CommentThread entityType="bid" entityId={bid.bid_id} />
        </Card>

        <Card className="p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-3">Activity</h2>
          <div className="space-y-2 max-h-64 overflow-auto">
            {activities.length === 0 && <div className="text-xs text-slate-400">No activity recorded yet.</div>}
            {activities.map((a) => (
              <div key={a.activity_id} className="flex items-start gap-3 text-xs" data-testid={`activity-${a.activity_id}`}>
                <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                <div>
                  <span className="text-slate-700">{a.message}</span>
                  <span className="text-slate-400"> — {a.user_name}, {fmtDate(a.at)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="space-y-6">
        <ScopeCompare bidId={bid.bid_id} />
        <Card className="p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 mb-3">Stage</h2>
          {bid.status === "sent" ? (
            <div className="text-sm text-slate-600">This bid is <b>Sent</b> and locked. Open a new revision to make changes.</div>
          ) : (
            <Select value={bid.status} onValueChange={changeStatus} disabled={!perms.canPrice}>
              <SelectTrigger data-testid="details-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {bid.loss_reason && <div className="text-xs text-rose-600 mt-2">Reason: {bid.loss_reason}</div>}
        </Card>

        <PriceTrajectoryCard bidId={bid.bid_id} refreshKey={`${bid.revision}-${bid.status}-${bid.active_version_id}`} />
        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">Sent revisions</h2>
            <Button variant="outline" size="xs" data-testid="revision-compare-open" disabled={revisions.length === 0}
              onClick={() => setShowCompare(true)}>Compare</Button>
          </div>
          <p className="text-xs text-slate-400 mb-3">Stored exactly as sent and never recalculated.</p>
          {bid.proposal_pdf_filename && (
            <a href={`${API_BASE}/bids/${bid.bid_id}/proposal-pdf`} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 mb-3" data-testid="archived-proposal-pdf">
              <FileText className="w-3.5 h-3.5" /> Open the archived proposal PDF (history)
            </a>
          )}
          <div className="space-y-2">
            {revisions.map((r) => (
              <div key={r.revision_id} className="flex items-center justify-between rounded border border-slate-100 px-3 py-2" data-testid={`revision-${r.revision}`}>
                <div>
                  <div className="text-sm font-medium text-slate-800">Revision {r.revision}</div>
                  <div className="text-[11px] text-slate-400">{fmtDate(r.sent_at)} · {r.sent_by}</div>
                  <div className="text-[11px] text-slate-400">Rates: {r.rate_library_name}</div>
                </div>
                <div className="text-sm font-mono font-medium text-slate-900">{money(r.totals?.total)}</div>
              </div>
            ))}
            {sealed.map((s) => (
              <div key={s.snapshot_id} className="flex items-center justify-between rounded border border-slate-100 px-3 py-2" data-testid={`sealed-snapshot-${s.snapshot_id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-800">{s.revision_label || "Snapshot"} <span className="text-[11px] text-slate-400">· legacy sealed</span></div>
                  <div className="text-[11px] text-slate-400">{fmtDate(s.locked_at || s.created_at)}{s.locked_note ? ` · ${s.locked_note}` : ""}</div>
                </div>
                <div className="text-sm font-mono font-medium text-slate-900">{money(s.total)}</div>
              </div>
            ))}
            {revisions.length === 0 && sealed.length === 0 && <div className="text-xs text-slate-400">Not sent yet.</div>}
          </div>
        </Card>
        <RevisionCompareDialog open={showCompare} onOpenChange={setShowCompare} bidId={bid.bid_id} revisions={revisions} />
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <div className="text-sm text-slate-800 mt-1.5">{children}</div>
    </div>
  );
}
